import { Component, OnInit, ViewChild, inject } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { Configuration } from "@app/app.constants";
import { NotificationModel } from "@app/mailmessages/models/notificationModel";
import { NotificationService } from "@app/mailmessages/service/notification.service";
import { CerrixEditorComponent } from "@app/shared/components/cerrix-editor/cerrix-editor.component";
import { FormValidationHelper } from "@app/shared/helpers/form-validation-helper";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { Pages } from "@constants/pages/Pages";
import { ModuleType } from "@enums/ModuleType";
import { getFormControl } from "@methods/CommonMethods";
import { isEmpty } from "@methods/isEmpty";
import { IdNameCombination } from "@models/generic/IdNameCombination";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { CerrixTreeItem } from "@models/tree/CerrixTreeItem";
import { TabService } from "@services/tabs/TabService";
import { TranslocoService, provideTranslocoScope } from "@jsverse/transloco";
import { ToastrService } from "ngx-toastr";
import { Observable, firstValueFrom } from "rxjs";
import { finalize } from "rxjs/operators";

@Component({
    selector: "uc-notification-detail",
    templateUrl: "./notification-detail.component.html",
    styleUrls: ["./notification-detail.component.scss"],
    standalone: false,
    providers: [provideTranslocoScope("mailmessages")]
})
export class NotificationDetailComponent implements OnInit {
    private _mmService = inject(NotificationService);
    private _route = inject(ActivatedRoute);
    private toastr = inject(ToastrService);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _promptService = inject(CerrixPromptService);
    private _transloco = inject(TranslocoService);

    @ViewChild("editor") editorComponent: CerrixEditorComponent;

    tab: TabModel;
    id: number;

    pageReady = false;
    module: ModuleType;
    isTaskModule: boolean = false;
    enableTypeField: boolean = false;

    notification: NotificationModel;
    keywords: string[];
    orgs: CerrixTreeItem[];
    mailEnabled: boolean;

    periods: IdNameCombination[] = [];
    types: IdNameCombination[] = [];
    subTypes: IdNameCombination[] = [];
    recipients: any[] = [];

    form: FormGroup;
    isEmpty = false;
    organizationRequest: Observable<CerrixTreeItem[]>;
    noOrganizationsSelectedDescription = Configuration.NoOrganizationsSelectedDescription;

    subjectHasFocus: boolean;

    constructor() {
        this.id = this._route.snapshot.params.id;
    }

    async ngOnInit() {
        await firstValueFrom(
            this._transloco.load(`mailmessages/${this._transloco.getActiveLang()}`)
        );

        const config = this.tab.config;
        if (!config) {
            this.toastr.error(
                this._transloco.translate("mailmessages.notificationDetail.errorOpeningDetailsMessage")
            );
            this.tab.close(false);
            return;
        }
        this.module = config.module;
        this.isTaskModule = this.module === ModuleType.Tasks;
        this.enableTypeField = config.module === ModuleType.BIM || config.module === ModuleType.IM;

        this._mmService.getNotification(this.id, this.module).subscribe((notification) => {
            if (this.id === 0) {
                notification.period = null;
                notification.subType = null;
                if (this.isTaskModule) {
                    // pbi=12764: only here default changes .....
                    notification.active = true;
                    notification.subject = 'Notification for task " [TaskName] "';
                }
            }
            this.keywords = notification.keywords;
            this.notification = notification;
            this.mailEnabled = this.notification.active;

            this._mmService.getNotificationStandingData(this.module).subscribe((data) => {
                this.periods = data.periods;
                this.types = data.types;
                this.subTypes = data.subTypes;
                this.reloadRecipients(notification.subType);
                this.initFormGroup(notification);
                this.pageReady = true;
            });
        });
        this.organizationRequest = this._mmService.getOrganizations();
    }

    initFormGroup(notification: NotificationModel) {
        this.form = new FormGroup({
            name: new FormControl(notification.name, [Validators.required]),
            active: new FormControl(notification.active),
            recipients: new FormControl(notification.recipients),
            subject: new FormControl(notification.subject, [Validators.required]),
            organizations: new FormControl(notification.organizations),
        });

        if (this.enableTypeField)
            this.form.addControl("types", new FormControl(notification.types, Validators.required));

        if (!this.isTaskModule) {
            this.form.addControl(
                "period",
                new FormControl(notification.period, Validators.required)
            );
            this.form.addControl(
                "subType",
                new FormControl(notification.subType, Validators.required)
            );
        }

        this.form.valueChanges.subscribe(() =>
            FormValidationHelper.mapToModel(this.form, this.notification)
        );
    }

    onChange(message: string) {
        this.notification.message = message;
    }

    addKeyword(keyword: string) {
        if (this.subjectHasFocus) {
            const ctrl = getFormControl(this.form, "subject");
            const value = ctrl.value;
            ctrl.setValue(value + " [" + keyword + "]");
        } else {
            this.editorComponent.insertText(" [" + keyword + "]");
        }
    }

    reloadRecipients(item: IdNameCombination | number) {
        if (!item) {
            return;
        }

        let id = 0;
        if (typeof item === "number") {
            id = item;
        } else {
            id = item.ID;
        }

        this._mmService
            .getRecipients(this.module, id, this.notification.recipients)
            .subscribe((users) => {
                this.recipients = users;
            });
    }

    async save() {
        this.isEmpty = false;
        if (!this.notification.organizations) {
            this.notification.organizations = [];
        }

        const messageEmpty = this.editorComponent.isEmpty();

        if (this.form.valid && !messageEmpty) {
            this.notification.id = this.id;

            const savingPrompt = this._promptService.loader(
                this._transloco.translate("mailmessages.notificationDetail.savingChangesMessage")
            );
            await firstValueFrom(this._mmService
                .saveNotification(this.notification)
                .pipe(finalize(() => savingPrompt.close())))
                .then((model: NotificationModel) => {
                    this.id = model.id;
                    this._tabService.refresh(this._pages.MailMessages);

                    this.toastr.success(
                        "",
                        this._transloco.translate("mailmessages.notificationDetail.updateCompletedTitle")
                    );
                })
                .catch((_) => {
                    this.toastr.error(
                        "",
                        this._transloco.translate("mailmessages.notificationDetail.updateFailedTitle")
                    );
                });
        } else {
            FormValidationHelper.markAllAsTouched(this.form);

            const validationErrors = FormValidationHelper.getFormControlErrors(this.form);
            let validationMessage = FormValidationHelper.getGeneralErrorMessage(validationErrors);

            if (messageEmpty) {
                this.isEmpty = true;
                validationMessage += `<br> - ${this._transloco.translate("mailmessages.notificationDetail.messageRequiredError")}`;
            }

            this.toastr.warning(
                validationMessage,
                this._transloco.translate("mailmessages.notificationDetail.saveFailedTitle"),
                { enableHtml: true }
            );
        }
    }

    deleteClick() {
        this._mmService.deleteNotification(this.id).subscribe((_) => {
            this._tabService.refresh(this._pages.MailMessages);
            this.toastr.success(
                this._transloco.translate("mailmessages.notificationDetail.notificationDeletedMessage")
            );
            this.tab.close(false);
        });
    }

    validate() {
        let isValid = true;
        const errors: string[] = [];
        if (isEmpty(this.notification.name)) {
            errors.push(this._transloco.translate("mailmessages.notificationDetail.nameRequiredError"));
            isValid = false;
        }

        if (isEmpty(this.notification.subject)) {
            errors.push(this._transloco.translate("mailmessages.notificationDetail.subjectRequiredError"));
            isValid = false;
        }

        if (errors.any()) {
            this.toastr.warning(
                errors.join("<br />"),
                this._transloco.translate("mailmessages.notificationDetail.validationsTitle"),
                { enableHtml: true }
            );
        }

        return isValid;
    }

    setFocusToSubject() {
        this.subjectHasFocus = true;
    }

    deFocusSubject() {
        this.subjectHasFocus = false;
    }
}
